/**
 * Baked-asset HYDRATION manager (Task A1). Re-selecting a template on the Mockups (batch
 * render) page used to re-run the multi-MB baked-asset download every time, because
 * `hydrateBakedAssets` had no in-flight dedup and never memoized failures (callers
 * fire-and-forget with `.catch(() => {})`). This module owns the cross-call state that
 * makes hydration idempotent and observable:
 *
 *  - `inflight`: one shared Promise per versionId, so concurrent callers JOIN the running
 *    hydrate instead of issuing a second set of fetches.
 *  - `failures`: a TTL'd failure memo, so a just-failed hydrate is not re-attempted on the
 *    very next re-select (until `FAILURE_TTL_MS` elapses, or the caller forces it).
 *  - `useHydrationStore`: a zustand status map the UI (a later task) subscribes to for
 *    per-template spinners / retry affordances. Absent key = idle.
 *
 * The `bakedSync.ts` hydrate wrapper drives these via the thin `mark*`/`setFailure`
 * helpers below; this is the only place the maps + zustand state are mutated together.
 *
 * Two invariants the helpers exist to keep, both learned from bugs where this state outlived
 * (or overstated) the assets it describes:
 *  - A memo must not survive the bake it memoizes — see `clearHydration`/`clearAllHydration`,
 *    which the Unsync and sign-out purges call.
 *  - A clear must be no wider than the success that triggered it, and an in-flight entry must
 *    not answer a caller that asked for MORE than it is doing — see `markReady`/`joinInflight`.
 */
import { create } from "zustand";
import { forgetBakeAbsence, forgetAllBakeAbsence } from "../templates/bakeDiagnostics.js";
import { mockupKey } from "./mockupStore.js";

/** Observable per-version hydration state. Absent key = idle (never started / reset). */
export type HydrationStatus =
  | { state: "hydrating" }
  | { state: "ready" }
  | { state: "failed"; at: number; message: string };

/**
 * The status record is a STABLE object mutated in place, with `version` as the change signal.
 * It used to be rebuilt with `{ ...prev.status, [id]: s }` on every write (AUD-3 §4), which is
 * O(n) per key and O(n²) across a hydrate sweep of hundreds of template×view keys — the
 * "Force sync all" path writes one status per key twice (hydrating, then ready).
 *
 * Selectors are unaffected: the selected VALUE (`s.status[key]`) still changes only for the key
 * that was written, so unrelated subscribers still don't re-render.
 */
const statusRecord: Record<string, HydrationStatus> = {};

interface HydrationState {
  /** Per-versionId status; absent key = idle. */
  status: Record<string, HydrationStatus>;
  /** Bumped on every write — the reference-stable record cannot signal a change by itself. */
  version: number;
  /** Set (or replace) one version's status. */
  set(id: string, s: HydrationStatus): void;
}

export const useHydrationStore = create<HydrationState>((set) => ({
  status: statusRecord,
  version: 0,
  set: (id, s) => {
    statusRecord[id] = s;
    set((prev) => ({ version: prev.version + 1 }));
  },
}));

/** How long a failed hydrate is memoized before it may be retried (without `force`). */
export const FAILURE_TTL_MS = 60_000;

/** One shared in-flight hydrate Promise per versionId (dedup). Reach it through
 *  {@link joinInflight}/{@link trackInflight} — a raw `get` cannot honour `force`. */
export const inflight = new Map<string, Promise<boolean>>();

/** The subset of `inflight` keys whose running hydrate was started with `force`. */
const forcedInflight = new Set<string>();

/** TTL'd failure memo per versionId. */
const failures = new Map<string, { at: number; message: string }>();

/** The key prefix every PER-VIEW key of `versionId` shares (`mockupKey` is the one place the
 *  key format lives, so the prefix is derived from it rather than re-spelled here). */
function viewKeyPrefix(versionId: string): string {
  return mockupKey(versionId, "");
}

/** The first still-memoized failure of ANY view of `versionId` (the bare aggregate key
 *  excluded), or undefined when every view is clean. */
function anyViewFailure(versionId: string): { at: number; message: string } | undefined {
  const prefix = viewKeyPrefix(versionId);
  for (const [key, rec] of failures) if (key.startsWith(prefix)) return rec;
  return undefined;
}

/** Mark a version as actively hydrating (drives the UI spinner). */
export function markHydrating(versionId: string): void {
  useHydrationStore.getState().set(versionId, { state: "hydrating" });
}

/**
 * Mark a version (or one `mockupKey`'d view of it) as hydrated + clear that key's failure memo.
 *
 * The clear is deliberately no WIDER than the success: `hydrateBakedAssets` publishes an
 * AGGREGATE status under the BARE `versionId` covering every view it was asked for, so a later
 * PRIMARY-view-only hydrate — which succeeds off the already-local fast path, without touching
 * the network — used to delete the aggregate failure a *different*, still-broken view had
 * recorded. The template then read as healthy: no row spinner, no retry affordance, and an
 * export that silently misses that view. While ANY per-view failure of this version survives,
 * the aggregate stays failed (re-asserted from the surviving view).
 */
export function markReady(versionId: string): void {
  const stillFailing = anyViewFailure(versionId);
  if (stillFailing) {
    failures.set(versionId, stillFailing);
    useHydrationStore.getState().set(versionId, { state: "failed", ...stillFailing });
    return;
  }
  failures.delete(versionId);
  useHydrationStore.getState().set(versionId, { state: "ready" });
}

/** Record a hydration failure in both the memo and the observable status. */
export function setFailure(versionId: string, message: string): void {
  const rec = { at: Date.now(), message };
  failures.set(versionId, rec);
  useHydrationStore.getState().set(versionId, { state: "failed", ...rec });
}

/** The memoized failure for a version, if any (used by bakedSync's TTL short-circuit). */
export function getFailure(versionId: string): { at: number; message: string } | undefined {
  return failures.get(versionId);
}

/** Selector helper: the memoized failure for a version, for UI retry affordances. */
export function hydrationFailure(versionId: string): { at: number; message: string } | undefined {
  return failures.get(versionId);
}

/**
 * The in-flight hydrate for `key` that a caller with this `force` intent MAY join, or undefined
 * when it must start its own.
 *
 * `inflight` is keyed by (version, view) ALONE, so "Force sync" pressed while an ordinary sync
 * was already running returned that ordinary run's promise — a cache-honouring hydrate that
 * short-circuits on the already-local fast path. Force then resolved `true` having downloaded
 * nothing, so a re-bake published onto the same version id stayed invisible on this device and
 * the recovery action reported success. A forced caller therefore never joins a non-forced run;
 * the reverse IS allowed, because a forced run is strictly fresher than what it asked for.
 */
export function joinInflight(key: string, force = false): Promise<boolean> | undefined {
  const existing = inflight.get(key);
  if (!existing) return undefined;
  if (force && !forcedInflight.has(key)) return undefined;
  return existing;
}

/** Register the hydrate now running for `key` (and whether it was FORCED — see joinInflight).
 *  Both the entry and its forced flag are dropped when the run settles — the store owns the
 *  whole lifecycle, so a stuck entry can never outlive the request that created it. */
export function trackInflight(key: string, run: Promise<boolean>, force = false): void {
  inflight.set(key, run);
  if (force) forcedInflight.add(key);
  void run.catch(() => {}).finally(() => {
    if (inflight.get(key) === run) inflight.delete(key);
    forcedInflight.delete(key);
  });
}

/**
 * Forget EVERYTHING this version's hydrates recorded — every view's failure memo and status,
 * plus the version-wide aggregate.
 *
 * Unsync ("remove from device") deletes the local bake but used to leave the memo behind. A
 * hydrate that had failed (offline, CORS, a 404 ref) stays memoized for `FAILURE_TTL_MS`, and
 * that memo short-circuits the NEXT hydrate before it fetches anything — so unsyncing and
 * immediately re-syncing to fix a broken template returned "done" having downloaded nothing,
 * and the template rendered design-only. The memo must not outlive the assets it describes.
 *
 * The in-flight map is deliberately NOT touched: its entries are owned (and deleted) by the
 * hydrate that created them, and dropping one here would let a duplicate download start
 * alongside the running one.
 */
export function clearHydration(versionId: string): void {
  // WS-P: the bake-absence breadcrumbs describe the same assets this memo does, so they expire
  // together. Without this, a remove → re-sync repair would announce nothing the second time.
  forgetBakeAbsence(versionId);
  const prefix = viewKeyPrefix(versionId);
  const keys = [versionId, ...[...failures.keys()].filter((k) => k.startsWith(prefix))];
  // In place, like `set` — the record's identity is the store's contract now (see statusRecord).
  for (const k of Object.keys(statusRecord)) {
    if (k === versionId || k.startsWith(prefix)) delete statusRecord[k];
  }
  for (const k of keys) failures.delete(k);
  useHydrationStore.setState((p) => ({ version: p.version + 1 }));
}

/**
 * Forget EVERY version's hydration state. The sign-out purge deletes the account's baked
 * mockups, so the memos describing them must go too: version ids are shared across accounts
 * (the platform catalog), and a failure memoized under account A would otherwise short-circuit
 * account B's very first sync of the same template — same defect as {@link clearHydration},
 * one workspace wider.
 */
export function clearAllHydration(): void {
  // WS-P: same reasoning as `clearHydration`, one workspace wider — the next account must be
  // able to hear about its own missing bakes.
  forgetAllBakeAbsence();
  failures.clear();
  for (const k of Object.keys(statusRecord)) delete statusRecord[k];
  useHydrationStore.setState((p) => ({ version: p.version + 1 }));
}

/** Test seam: clear all in-flight/failure/status state between tests. The `version` counter
 *  is zeroed too, so a test can assert an exact write count from a known start. */
export function resetHydrationForTest(): void {
  inflight.clear();
  forcedInflight.clear();
  clearAllHydration();
  useHydrationStore.setState({ version: 0 });
}
